<mat-toolbar color="primary">
  <span id="start-møte-transkripsjon-dialog-tittel">{{ "Start transkripsjon av møte"|translate }}</span>
  <button mat-icon-button [attr.aria-label]="'Lukk dialog'|translate" (click)="avbryt()">
    <mat-icon fontSet="ion-ios-close-empty" class="mat-icon--ionic-ikon"/>
  </button>
</mat-toolbar>

<mat-stepper class="e2e-startmøtetranskripsjondialog" (selectionChange)="vedStegbytte($event)" #steg>

  <!--steg 1: velg mikrofon-->
  <mat-step [label]="'Velg mikrofon'|translate">
    <mat-dialog-content class="skjema" data-testid="steg-velg-mikrofon">
      @if (fortsettelse != null) {
        <inline-melding type="advarsel" data-testid="fortsett-opptak">
          @switch (fortsettelse) {
            @case ('ingenLyd') { {{ 'Opptaket er stoppet fordi mikrofonen ikke fanget opp lyd.'|translate }} }
            @case ('mikrofonFrakoblet') { {{ 'Opptaket er stoppet fordi mikrofonen ble koblet fra.'|translate }} }
            @case ('mikrofonDempet') { {{ 'Opptaket er stoppet fordi mikrofonen er dempet.'|translate }} }
            @case ('avbrutt') { {{ 'Opptaket stoppet uventet.'|translate }} }
          }
          {{ 'Det som er lastet opp så langt er tatt vare på. Velg mikrofon og start opptaket på nytt.'|translate }}
        </inline-melding>
      } @else if (harEksisterendeOpptak()) {
        <inline-melding type="advarsel" data-testid="eksisterende-opptak">
          <div class="eksisterende-opptak">
            <span>
              {{ 'Møtet har allerede {antallOpptak} lydopptak ({varighet}). Et nytt opptak kommer i tillegg til disse.'
                | translate:{antallOpptak: transkripsjonsinformasjon()!.antallOpptak, varighet: (transkripsjonsinformasjon()!.totalVarighet | duration : 'timerMinutterKort')} }}
            </span>
            <button mat-stroked-button color="warn" type="button" data-testid="slett-opptak"
                    [disabled]="sletterOpptak()"
                    [bekreftKlikk]="'Ja, slett lydopptakene'|translate"
                    (klikk)="slettEksisterendeOpptak()">
              {{ 'Slett eksisterende lydopptak'|translate }}
            </button>
          </div>
        </inline-melding>
      }

      <fieldset>
        <mikrofon-velger [(valgtDeviceId)]="valgtDeviceId" (vedFeil)="vedEnhetsfeil($event)"/>

        <mat-form-field class="spraakvelger">
          <mat-label>{{ 'Språk det snakkes i møtet'|translate }}</mat-label>
          <mat-select [formControl]="valgtSpraak" data-testid="velg-spraak">
            @for (spraak of snakkedeSpraak; track spraak.verdi) {
              <mat-option [value]="spraak.verdi">{{ spraak.navn|translate }}</mat-option>
            }
          </mat-select>
          <mat-hint>{{ 'Transkripsjonen blir best når riktig språk er valgt'|translate }}</mat-hint>
        </mat-form-field>
      </fieldset>

      @if (enhetsfeil() != null) {
        <p class="feil" data-testid="enhetsfeil">{{ enhetsfeil() }}</p>
      }

    </mat-dialog-content>

    <mat-dialog-actions align="center">
      <button mat-button type="button" (click)="avbryt()">{{ "Avbryt"|translate }}</button>
      <button mat-raised-button color="primary" type="button" cdkFocusInitial matStepperNext data-testid="neste">
        {{ "Neste"|translate }}
      </button>
    </mat-dialog-actions>
  </mat-step>

  <!--steg 2: test mikrofonen med et kort opptak som transkriberes på server-->
  <mat-step [label]="'Test mikrofon'|translate">
    <mat-dialog-content class="skjema" data-testid="steg-test-mikrofon">
      <p>
        {{ 'Ta et kort testopptak for å sjekke at mikrofonen fungerer. Start testopptaket og snakk som normalt i {sekunder} sekunder, så vises det som ble oppfattet.'|translate: {sekunder: testopptaksekunder} }}
      </p>

      <div class="testopptak" data-testid="testopptak">
        @switch (testtilstand()) {
          @case ('tarOpp') {
            <div class="tar-opp" role="status">
              <mikrofon-puls [deviceId]="valgtDeviceId()"/>
              <mat-progress-bar mode="determinate" [value]="testfremdrift()"/>
              <span class="nedtelling">{{ 'Tar opp, {sekunder} sekunder igjen …'|translate: {sekunder: gjenstaaendeSekunder()} }}</span>
            </div>
          }
          @case ('transkriberer') {
            <div class="transkriberer" role="status">
              <mat-progress-spinner mode="indeterminate" diameter="20"/>
              <span>{{ 'Transkriberer testopptaket …'|translate }}</span>
            </div>
          }
          @case ('ferdig') {
            @if (testtranskripsjon()) {
              <p class="forklaring">{{ 'Dette ble oppfattet:'|translate }}</p>
              <blockquote class="testtranskripsjon" data-testid="testtranskripsjon">{{ testtranskripsjon() }}</blockquote>
              <p class="forklaring">{{ 'Stemmer teksten omtrent med det du sa, fungerer mikrofonen. Hvis ikke kan du gå tilbake og velge en annen mikrofon.'|translate }}</p>
            } @else {
              <inline-melding type="advarsel" data-testid="ingen-tale">
                {{ 'Det ble ikke gjenkjent noe tale i testopptaket. Kontroller at riktig mikrofon er valgt og at den ikke er dempet, og forsøk på nytt.'|translate }}
              </inline-melding>
            }
          }
          @case ('feilet') {
            <p class="feil" data-testid="testfeil">{{ testfeil() }}</p>
          }
        }

        <button mat-stroked-button color="primary" type="button" data-testid="start-testopptak"
                [disabled]="testPaagaar()" (click)="taTestopptak()">
          <mat-icon>mic</mat-icon>
          @if (testtilstand() === 'ikkeStartet') {
            {{ 'Start testopptak'|translate }}
          } @else {
            {{ 'Ta nytt testopptak'|translate }}
          }
        </button>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="center">
      <button mat-button type="button" matStepperPrevious>{{ "Tilbake"|translate }}</button>
      <button mat-raised-button color="primary" type="button" matStepperNext data-testid="neste" [disabled]="testPaagaar()">
        {{ "Neste"|translate }}
      </button>
    </mat-dialog-actions>
  </mat-step>

  <!--steg 3: informasjon om hva som skjer når opptaket avsluttes, og start-->
  <mat-step [label]="'Start opptak'|translate">
    <mat-dialog-content class="skjema" data-testid="steg-start">
      <p>{{ 'Lyden fra møtet tas opp via valgt mikrofon og sendes til Styreplan for transkribering fortløpende.'|translate }}</p>
      <p data-testid="protokollforslag-informasjon">
        {{ 'Når du avslutter opptaket kan du velge å få generert et forslag til protokoll basert på det som ble sagt i møtet. Forslaget blir tilgjengelig på siden for redigering av protokollen, der du kan gå gjennom og tilpasse det før protokollen ferdigstilles.'|translate }}
      </p>
      <tips>
        <p>{{ 'La nettleserfanen være åpen mens møtet pågår. Opptaket kan settes på pause, og du får beskjed dersom mikrofonen slutter å fange opp lyd.'|translate }}</p>
      </tips>

      @if (skjemalaasErrorRespons() != null) {
        <inline-melding type="advarsel" voksHorisontalt="true" data-testid="opptak-startet-av-annen">
          <div class="eksisterende-lås">
            {{ '{av} har allerede startet et opptak av dette møtet ({dato}, {relativDato})'|translate: {dato: skjemalaasErrorRespons()!.konflikt.oppdatertDato|zoneddatetime, relativDato: skjemalaasErrorRespons()!.konflikt.oppdatertDato|zoneddatetime_relativ|async, av: skjemalaasErrorRespons()!.konflikt.eier.navn} }}
            <mat-slide-toggle [formControl]="overtaSkjemalaas">{{ 'Ignorer dette og start et ytterligere opptak, merk at dette kan redusere kvaliteten på transkripsjonen vesentlig'|translate }}</mat-slide-toggle>
          </div>
        </inline-melding>
      }

      @if (sisteFeil() != null) {
        <p class="feil" data-testid="feil">{{ sisteFeil() }}</p>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="center">
      <button mat-button type="button" matStepperPrevious>{{ "Tilbake"|translate }}</button>
      <button mat-raised-button color="primary" type="button" (click)="start()" data-testid="start" [disabled]="startKnappDeaktivert()">
        {{ 'Start transkripsjon'|translate }}
      </button>
    </mat-dialog-actions>
  </mat-step>
</mat-stepper>
